<template>
  <div class="settings">
    <a-card title="个人信息">
      <a-form layout="vertical">
        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item label="统一用户 ID">
              <a-input :value="gatewayProfile.authUserId" disabled />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="用户名">
              <a-input :value="gatewayProfile.username" disabled />
            </a-form-item>
          </a-col>
        </a-row>
        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item label="显示名称">
              <a-input :value="gatewayProfile.displayName" disabled />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="邮箱">
              <a-input :value="gatewayProfile.email" disabled />
            </a-form-item>
          </a-col>
        </a-row>
        <div class="hint">身份信息展示来自 `/bff/me`，登录、登出和权限由统一认证网关管理。</div>
      </a-form>
    </a-card>

    <a-card class="mt" title="搜索记录">
      <a-form layout="vertical">
        <a-form-item label="记录空答案">
          <a-switch v-model:checked="recordEmptyAnswer" />
          <div class="hint">开启后，题库和外部来源都未命中时，空答案也会写入回收记录。</div>
        </a-form-item>
      </a-form>
    </a-card>

    <a-card class="mt" title="题库顺序与开关">
      <template #extra>
        <a-button type="primary" @click="saveProviders" :loading="saving">保存</a-button>
      </template>
      <div v-if="!profile.allowPaid && !profile.allowAi" class="empty-tip">当前账号未被授权使用付费/AI题库</div>
      <div class="order-block" v-if="searchList.length">
        <div class="block-title">题库顺序</div>
        <div class="sub-title">拖拽调整顺序，关闭后将跳过该题库</div>
        <draggable v-model="searchOrder" item-key="provider" class="drag-list" handle=".drag-handle">
          <template #item="{ element }">
            <div class="drag-item">
              <div class="drag-handle">⋮⋮</div>
              <div class="drag-name">{{ element.name || labelMap[element.provider] || element.provider }}</div>
              <a-switch v-model:checked="providerSwitch[element.provider]" />
            </div>
          </template>
        </draggable>
      </div>
      <div class="order-block" v-if="aiList.length">
        <div class="block-title">AI 题库顺序</div>
        <div class="sub-title">AI 将在常规题库无结果时继续尝试</div>
        <draggable v-model="aiOrder" item-key="provider" class="drag-list" handle=".drag-handle">
          <template #item="{ element }">
            <div class="drag-item">
              <div class="drag-handle">⋮⋮</div>
              <div class="drag-name">{{ element.name || labelMap[element.provider] || element.provider }}</div>
              <a-switch v-model:checked="providerSwitch[element.provider]" />
            </div>
          </template>
        </draggable>
      </div>
    </a-card>
  </div>
</template>

<script>
import { defineComponent, onMounted, reactive, ref } from 'vue'
import draggable from 'vuedraggable'
import { message } from 'ant-design-vue'
import { getGatewayMe, getUserSettings, updateUserSettings } from '@/api/TikuComponentApi'
import { useSessionStore } from '@/store/session'

export default defineComponent({
  name: 'UserSettings',
  components: { draggable },
  setup() {
    const session = useSessionStore()
    const profile = reactive({
      authUserId: '',
      isAdmin: false,
      allowAi: true,
      allowPaid: true,
    })
    const gatewayProfile = reactive({
      authUserId: '',
      username: '',
      displayName: '',
      email: '',
    })
    const providerSwitch = reactive({})
    const searchOrder = ref([])
    const aiOrder = ref([])
    const searchList = ref([])
    const aiList = ref([])
    const recordEmptyAnswer = ref(true)
    const saving = ref(false)
    const labelMap = {
      local: '本地题库',
      buguake: '不挂科题库',
      icodef: 'icodef题库',
      wanneng: '万能题库',
      tikuhai: '题库海',
      aidian: '爱点题库',
      enncy: 'enncy题库',
      lemon: '柠檬题库',
      custom: '自定义题库',
      openai: 'OpenAI',
      deepseek: 'DeepSeek',
      gemini: 'Gemini',
      qwen: '千问',
    }

    const buildOrder = (providers = [], baseOrder = [], switches = {}) => {
      const mapped = new Map()
      providers.forEach((p) => mapped.set(p.provider, p))
      const ordered = []
      const used = new Set()
      baseOrder.forEach((name) => {
        if (mapped.has(name) && !used.has(name)) {
          ordered.push(mapped.get(name))
          used.add(name)
        }
      })
      providers.forEach((p) => {
        if (!used.has(p.provider)) ordered.push(p)
      })
      ordered.forEach((p) => {
        if (switches[p.provider] === undefined) {
          providerSwitch[p.provider] = true
        }
      })
      return ordered
    }

    const syncGatewayProfile = (data = {}) => {
      gatewayProfile.authUserId = data.sub || data.username || data.preferred_username || ''
      gatewayProfile.username = data.username || data.preferred_username || data.email || ''
      gatewayProfile.displayName = data.nickname || data.name || gatewayProfile.username || gatewayProfile.authUserId
      gatewayProfile.email = data.email || ''
      session.applyGatewayProfile(data)
    }

    const fetchSettings = () => {
      Promise.all([getGatewayMe(), getUserSettings()]).then(([gatewayRes, settingRes]) => {
        if (gatewayRes?.status === 200 && gatewayRes.data) {
          syncGatewayProfile(gatewayRes.data)
        }
        if (settingRes?.status !== 200) {
          message.error(settingRes?.message || '获取设置失败')
          return
        }
        const data = settingRes.data || {}
        profile.authUserId = data.user?.authUserId || ''
        profile.isAdmin = data.user?.isAdmin === true
        profile.allowAi = data.user?.allowAi !== false
        profile.allowPaid = data.user?.allowPaid !== false
        session.applyUserSettings(data)

        const switches = data.settings?.providerSwitch || {}
        Object.keys(providerSwitch).forEach((k) => delete providerSwitch[k])
        Object.keys(switches || {}).forEach((k) => { providerSwitch[k] = switches[k] })

        const combinedOrder = (data.settings?.searchOrder && data.settings.searchOrder.length)
          ? data.settings.searchOrder
          : (data.defaultOrder || [])
        recordEmptyAnswer.value = data.settings?.recordEmptyAnswer ?? true

        searchList.value = data.providers?.search || []
        aiList.value = data.providers?.ai || []

        searchOrder.value = buildOrder(searchList.value, combinedOrder, providerSwitch)
        aiOrder.value = buildOrder(aiList.value, combinedOrder, providerSwitch)
      }).catch(() => {
        message.error('获取设置失败')
      })
    }

    const saveProviders = () => {
      saving.value = true
      const payload = {
        searchOrder: [...searchOrder.value.map(i => i.provider), ...aiOrder.value.map(i => i.provider)],
        providerSwitch: { ...providerSwitch },
        recordEmptyAnswer: recordEmptyAnswer.value,
      }
      updateUserSettings(payload).then((res) => {
        if (res?.status === 200) {
          message.success('已保存')
          fetchSettings()
        } else {
          message.error(res?.message || '保存失败')
        }
      }).finally(() => {
        saving.value = false
      })
    }

    onMounted(() => {
      fetchSettings()
    })

    return {
      profile,
      gatewayProfile,
      providerSwitch,
      searchOrder,
      aiOrder,
      searchList,
      aiList,
      recordEmptyAnswer,
      saving,
      labelMap,
      saveProviders,
    }
  }
})
</script>

<style scoped>
.settings {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.mt {
  margin-top: 12px;
}
.form-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}
.hint {
  color: #888;
  font-size: 12px;
}
.order-block {
  margin-top: 12px;
}
.block-title {
  font-weight: 600;
  margin-bottom: 4px;
}
.sub-title {
  color: #888;
  font-size: 13px;
  margin-bottom: 10px;
}
.drag-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.drag-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  background: #fafafa;
}
.drag-handle {
  cursor: grab;
  color: #999;
  font-weight: 700;
}
.drag-name {
  flex: 1;
}
.empty-tip {
  color: #999;
}
</style>
